import AddIcon from '@atlaskit/icon/core/add';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { DraggableList } from '@databases/draggable-list/DraggableList';
import { FormButton } from '@databases/form/FormComponents';
import { messages } from '@databases/intl/messages';
import { produce } from 'immer';
import React, { type ReactElement, type ReactNode, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl-next';

const containerStyles = xcss({
  maxHeight: 'none',
  overflowY: 'auto',
  overflowX: 'auto',
  paddingBlock: 'space.075'
});

const scrollingContainerStyles = xcss({
  maxHeight: '395px',
  overflowX: 'hidden'
});

const compactContainerStyles = xcss({
  paddingBlock: 'space.0'
});

const addButtonStyles = xcss({
  background: 'elevation.surface.overlay',
  paddingBlock: 'space.075'
});

const compactAddButtonStyles = xcss({
  marginInlineEnd: 'space.negative.100',
  paddingBlock: 'space.0'
});

const dividerStyles = xcss({
  borderTopWidth: 'border.width',
  borderTopStyle: 'solid',
  borderTopColor: 'color.border'
});

export interface EditableListProps<T, U> extends Omit<
  RawEditableListProps<T, U>,
  'onAdd' | 'onValueChange' | 'onDelete' | 'onReorder' | 'autoFocus'
> {
  onItemChange?: (value: T, newIndex: number, delta: U, allValues: T[]) => void;
  onAddNewItem?: (value: T, allValues: T[]) => void;
  onRemoveItem?: (value: T, removeIndex: number, allValues: T[]) => void;
  onReorder?: (value: T, currentIndex: number, newIndex: number, allValues: T[]) => void;
  getNewValue: () => T;
  autoFocus?: boolean;
  isHovering?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  allowDuplicates?: boolean;
  showHoverBackground?: boolean;
}

/**
 * Renders a vertical list of text input fields that can be edited, reordered, deleted and added.
 */
export function EditableList<T, U>({
  values,
  onItemChange,
  onAddNewItem,
  onRemoveItem,
  onReorder,
  getNewValue,
  autoFocus,
  isHovering,
  allowDuplicates = true,
  showHoverBackground = false,
  ...props
}: EditableListProps<T, U>): ReactElement {
  const focusI = useRef<number | undefined>();

  const handleAdd = useCallback(() => {
    focusI.current = values.length;
    const newValue = getNewValue();
    const allValues = [...values, newValue];
    onAddNewItem?.(newValue, allValues);
  }, [getNewValue, onAddNewItem, values]);

  const [duplicateId, setDuplicateId] = React.useState<string | undefined>(undefined);
  const handleValueChange = useCallback(
    (i: number, val: T, delta: U) => {
      const newValues = produce(values, (newValues) => {
        newValues[i] = val as any;
      });
      const filteredLabels = newValues
        .filter((item: any) => item.id !== (val as any).id)
        .map((item: any) => item.label);
      if (!allowDuplicates) {
        if (filteredLabels.includes((val as any).label)) {
          setDuplicateId((val as any).id);
          return;
        } else {
          setDuplicateId(undefined);
        }
      }
      onItemChange?.(val, i, delta, newValues);
    },
    [onItemChange, values, allowDuplicates]
  );

  const handleReorder = useCallback(
    (i: number, newIdx: number) => {
      const newValues = produce(values, (newValues) => {
        const [removed] = newValues.splice(i, 1);
        newValues.splice(newIdx, 0, removed);
      });
      onReorder?.(values[i], i, newIdx, newValues);
      // If the reordered item had the focus, move the focus to same item so that the user can blur properly to exit the inline edit field
      focusI.current = newIdx;
    },
    [onReorder, values]
  );

  const handleDelete = useCallback(
    (i: number) => {
      // If the deleted item had the focus, move the focus to another item so that the user can blur properly to exit the inline edit field
      focusI.current = i === values.length - 1 && i > 0 ? i - 1 : i;
      setDuplicateId(undefined);

      const newValues = produce(values, (newValues) => {
        newValues.splice(i, 1);
      });
      onRemoveItem?.(values[i], i, newValues);
    },
    [onRemoveItem, values]
  );

  let resolvedFocusI: number | 'addButton' | undefined =
    focusI.current ?? (autoFocus ? 0 : undefined);
  if (typeof resolvedFocusI === 'number' && resolvedFocusI >= values.length) {
    resolvedFocusI = values.length === 0 ? 'addButton' : values.length - 1;
  }
  focusI.current = undefined;

  return (
    <RawEditableList<T, U>
      {...props}
      values={values}
      onAdd={handleAdd}
      onValueChange={handleValueChange}
      onDelete={handleDelete}
      onReorder={handleReorder}
      autoFocus={resolvedFocusI}
      isHovering={isHovering}
      duplicateId={duplicateId}
      showHoverBackground={showHoverBackground}
    />
  );
}

interface RawEditableListProps<T, U> {
  getId: (value: T) => string;
  values: T[];
  onAdd?: () => void;
  onValueChange?: (i: number, value: T, delta: U) => void;
  onDelete?: (i: number) => void;
  onReorder?: (i: number, newIdx: number) => void;
  renderField: (data: {
    i: number;
    value: T;
    autoFocus: boolean;
    onChange: (value: T, delta: U) => void;
    isReadonly: boolean;
    isDisabled: boolean;
    duplicateId?: string | undefined;
  }) => ReactNode;
  /** The index of the item to autofocus. */
  autoFocus?: number | 'addButton';
  addLabel?: string;
  noAdd?: boolean;
  compactAddButton?: boolean;
  addButtonIconSize?: 'small' | 'medium';
  addButtonTextSize?: 'small' | 'medium' | 'large';
  addButtonTextWeight?: 'regular' | 'medium' | 'semibold' | 'bold';
  compactListPadding?: boolean;
  compactDeleteButton?: boolean;
  /** If true, show a divider above the add button */
  showDivider?: boolean;
  noDelete?: boolean;
  deleteTooltip?: string;
  isHovering?: boolean;
  dragAxis?: 'x' | 'y';
  yAxisBounds?: boolean;
  isReadonly?: boolean;
  isDisabled?: boolean;
  verticalScroll?: boolean;
  activeId?: string;
  editingId?: string;
  errorId?: string;
  showUnsavedView?: boolean;
  duplicateId?: string | undefined;
  showHoverBackground?: boolean;
}

/**
 * Renders a vertical list of text input fields that can be edited, reordered, deleted and added.
 * Does not manage the array of items, but offers event handlers for the individual user actions to react to.
 */
export function RawEditableList<T, U>({
  getId,
  values,
  onAdd,
  onValueChange,
  onDelete,
  onReorder,
  autoFocus,
  renderField,
  addLabel,
  noAdd,
  compactAddButton = false,
  addButtonIconSize,
  addButtonTextSize,
  addButtonTextWeight,
  compactListPadding = false,
  compactDeleteButton = false,
  noDelete,
  deleteTooltip,
  isHovering = true,
  showDivider = false,
  isReadonly = false,
  isDisabled = false,
  verticalScroll = false,
  activeId = undefined,
  editingId = undefined,
  errorId = undefined,
  duplicateId,
  showHoverBackground = false
}: RawEditableListProps<T, U>): ReactElement {
  const { formatMessage } = useIntl();

  const lastYPosition = useRef(0);
  const handleAdd = useCallback(() => {
    lastYPosition.current = 0;
    onAdd?.();
  }, [onAdd]);

  return (
    <Flex direction="column">
      <Box
        xcss={[
          containerStyles,
          compactListPadding && compactContainerStyles,
          verticalScroll && scrollingContainerStyles
        ]}
      >
        <DraggableList
          data-interactive
          onReorder={onReorder}
          renderField={renderField}
          values={values}
          getId={getId}
          isReadonly={isReadonly}
          isDisabled={isDisabled}
          autoFocus={autoFocus}
          onValueChange={onValueChange}
          noDelete={noDelete}
          deleteTooltip={deleteTooltip}
          onDelete={onDelete}
          activeId={activeId}
          errorId={errorId}
          editingId={editingId}
          isHovering={isHovering}
          duplicateId={duplicateId}
          showHoverBackground={showHoverBackground}
          compactDeleteButton={compactDeleteButton}
        />
      </Box>
      {!noAdd && !isReadonly && duplicateId === undefined && (
        <Box
          testId="editable-list-add-button-container"
          xcss={[
            addButtonStyles,
            compactAddButton && compactAddButtonStyles,
            showDivider && dividerStyles
          ]}
        >
          <FormButton
            iconBefore={<AddIcon label="" size={addButtonIconSize} />}
            isCompact={compactAddButton}
            onClick={handleAdd}
            textSize={addButtonTextSize}
            textWeight={addButtonTextWeight}
            text={
              addLabel ? addLabel : formatMessage(messages['field-settings.tag-add-item'])
            }
            testId="editable-list-add-button"
          />
        </Box>
      )}
    </Flex>
  );
}
